import React, { forwardRef, useEffect, useState } from 'react'
import Country from '../../enums/Country'

import {
  hasCharCountLessThanOrEqualTo,
  isValidAddressCharacters,
  isValidAustralianHomeNumber,
  isValidAustralianMobileNumber,
  isValidCharacters,
  isValidContactAddressLine1,
  isValidContactAddressLine2,
  isValidContactAddressLine3,
  isValidContactAustraliaSuburb,
  isValidContactAustralianPostcode,
  isValidContactAustralianState,
  isValidContactCountry,
  isValidContactInternationalPostcode,
  isValidContactInternationalState,
  isValidContactInternationalSuburb,
  isValidCountryForContactResidencyType,
  isValidCountryForPermanentResidencyType,
  isValidEdgesCharacter,
  isValidEmail,
  isValidEmailDomain,
  isValidInternationalHomeNumber,
  isValidInternationalMobileNumber,
  isValidInternationalNumber,
  isValidPermanentAustralianPostcode,
  isValidPermanentAustralianState,
  isValidPermanentAustralianSuburb,
  isValidPermanentCountry,
  isValidPermanentInternationalPostcode,
  isValidPermanentInternationalState,
  isValidPermanentInternationalSuburb,
} from '../../helpers/validation'

import {
  getDefaultContactAddress,
  getTooltipBySlug,
  isDomesticStudent,
  isInternationalOnshoreStudent,
  mergeContactDetails
} from '../../helpers/form'
import { getTooltipsForPage } from '../../services/reservoir'
import Address from './Address'
import HelpText from './HelpText'
import Modal from './Modal'

import { Info, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Radio,
  RadioGroup,
  Checkbox,
  TextField,
  Select,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  FormGroup,
  FormControlLabel
} from '@mui/material';

import * as Yup from 'yup';
import AddressValidationStatus from '../../enums/AddressValidationStatus'

const SettingsForm = props => {
  const validationSchema = Yup.object().shape({
    preferredName: Yup.string()
      .required('Name should not be empty.')
      .test('charCount', 'Name must be less than 30 characters long.', (value, context) => hasCharCountLessThanOrEqualTo(value, 30))
      .test('isValidEdgesCharacter', 'It must start and end with a letter (A-Z a-z).', (value, context) => isValidEdgesCharacter(value))
      .test('isValidCharacters', 'Please use only letters, spaces, apostrophes, or hyphens.', (value, context) => isValidCharacters(value)),
    
    mobileNumber: Yup.string()
      .test('isValidAustralianMobileNumber', 'Please enter a valid 10 digit Australian phone number.', (value, context) => isValidAustralianMobileNumber(value, context))
      .test('isValidInternationalMobileNumber', 'Overseas phone numbers must be between 1 and 20 characters long.', (value, context) => isValidInternationalMobileNumber(value, context)),
    
    homePhoneNumber: Yup.string()
      .test('isValidAustralianHomeNumber', 'Please enter in a valid Australian phone number.', (value, context) => isValidAustralianHomeNumber(value, context))
      .test('isValidInternationalHomeNumber', 'Overseas phone numbers must be between 1 and 20 characters long.', (value, context) => isValidInternationalHomeNumber(value, context)),
    
    emailAddress: Yup.string()
      .required('Email should not be empty.')
      .max(100, 'Email address must be less than 100 characters long.')
      .test('Email address must be valid.', (value, context) => isValidEmail(value))
      .test('isValidEmailDomain', 'Personal email cannot be a VU email address. Please enter an alternative personal email address.', (value, context) => isValidEmailDomain(value)),
    
    permanentCountry: Yup.string()
      .test('isValidPermanentCountry', 'Please enter a valid country.', (value, context) => isValidPermanentCountry(value, props.countryCodes))
      .test('isValidCountryForPermanentResidencyType', 'Please enter a valid country.', (value, context) => isValidCountryForPermanentResidencyType(value, context)),
    
    permanentAddressLine1: Yup.string()
      .required('Address should not be empty.')
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value))
      .test('isValidPermanentAddressLine1', 'Street address must be less than 50 characters long.', (value, context) => hasCharCountLessThanOrEqualTo(value, 50)),
    
    permanentAddressLine2: Yup.string()
      .test('permanentAddressLine2', 'Address name must be less than 50 characters long.', (value, context) => hasCharCountLessThanOrEqualTo(value, 50))
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value)),
    
    permanentAddressLine3: Yup.string()
      .test('permanentAddressLine2', 'Address name must be less than 50 characters long.', (value, context) => hasCharCountLessThanOrEqualTo(value, 50))
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value)),
    
    permanentSuburb: Yup.string()
      .test('isValidPermanentAustraliaSuburb', 'Please provide a valid Australian suburb.', (value, context) => isValidPermanentAustralianSuburb(value, context))
      .test('isValidPermanentInternationalSuburb', 'Please provide a valid suburb.', (value, context) => isValidPermanentInternationalSuburb(value, context)),
    
    permanentState: Yup.string()
      .test('isValidPermanentAustralianState', 'Please select a valid state.', (value, context) => isValidPermanentAustralianState(value, context))
      .test('isValidPermanentInternationalState', 'Overseas states must be less than 40 characters.', (value, context) => isValidPermanentInternationalState(value, context)),
    
    permanentPostcode: Yup.string()
      .test('isValidPermanentAustralianPostcode', 'Australian postcodes must be 4 digits.', (value, context) => isValidPermanentAustralianPostcode(value, context))
      .test('isValidPermanentInternationalPostcode', 'Overseas postcodes must be less than 10 characters.', (value, context) => isValidPermanentInternationalPostcode(value, context)),
    
    contactCountry: Yup.string()
      .test('isValidContactCountry', 'Please enter a valid country.', (value, context) => isValidContactCountry(value, context, props.countryCodes))
      .test('isValidCountryForContactResidencyType', 'Please enter a valid country.', (value, context) => isValidCountryForContactResidencyType(value, context)),
    
    contactAddressLine1: Yup.string()
      .test('isValidContactAddressLine1', 'Street address must be less than 50 characters long.', (value, context) => isValidContactAddressLine1(value, context))
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value)),
    
    contactAddressLine2: Yup.string()
      .test('isValidContactAddressLine2', 'Please provide a street address that is less than 50 characters long.', (value, context) => isValidContactAddressLine2(value, context))
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value)),
    
    contactAddressLine3: Yup.string()
      .test('isValidContactAddressLine3', 'Please provide a address that is less than 50 characters long.', (value, context) => isValidContactAddressLine3(value, context))
      .test('isValidAddressCharacters', 'Please use only letters, numbers, spaces, and basic punctuation.', (value, context) => isValidAddressCharacters(value)),
    
    contactSuburb: Yup.string()
      .test('isValidContactAustraliaSuburb', 'Please provide a valid Australian suburb.', (value, context) => isValidContactAustraliaSuburb(value, context))
      .test('isValidContactInternationalSuburb', 'Please provide a valid suburb.', (value, context) => isValidContactInternationalSuburb(value, context)),
    
    contactState: Yup.string()
      .test('isValidContactAustralianState', 'Please select a valid state.', (value, context) => isValidContactAustralianState(value, context))
      .test('isValidContactInternationalState', 'Overseas states must be less than 40 characters.', (value, context) => isValidContactInternationalState(value, context)),
    
    contactPostcode: Yup.string()
      .test('isValidContactAustralianPostcode', 'Australian postcodes must be 4 digits.', (value, context) => isValidContactAustralianPostcode(value, context))
      .test('isValidContactInternationalPostcode', 'Overseas postcodes must be less than 10 characters.', (value, context) => isValidContactInternationalPostcode(value, context)),
    
    contactName: Yup.string()
      .required('Contact name should not be empty.')
      .test('charCount', 'Contact name must be less than 40 characters long.', (value, context) => hasCharCountLessThanOrEqualTo(value, 40))
      .test('isValidEdgesCharacter', 'It must start and end with a letter (A-Z a-z).', (value, context) => isValidEdgesCharacter(value))
      .test('isValidCharacters', 'Please use only letters, spaces, apostrophes, or hyphens.', (value, context) => isValidCharacters(value)),
    
    contactNumber: Yup.string()
      .test('isValidInternationalNumber', 'Contact number must be between 1 and 20 characters long.', (value, context) => isValidInternationalNumber(value)),
    
    contactRelationship: Yup.string()
      .required('Contact relationship should not be empty.'),
  })

  const [tooltips, setTooltips] = useState([]);
  const [pronounsList, setPronounsList] = useState([]);
  const [affirmedGenderList, setAffirmedGenderList] = useState([]);
  const [hideChosenDetaiils, setHideChosenDetaiils] = useState(false);
  const [showChosenAffirmedGenderModal, setShowChosenAffirmedGenderModal] = useState(false);
  const [showChosenPronounsModal, setShowChosenPronounsModal] = useState(false);
  const [showChosenLastNameModal, setShowChosenLastNameModal] = useState(false);
  const [showChosenFirstNameModal, setShowChosenFirstNameModal] = useState(false);

  const [values, setValues] = useState({
    preferredName: '',
    chosenFirstName: '',
    chosenLastName: '',
    chosenPronouns: '',
    chosenAffirmedGender: '',
    mobileNumber: '',
    homePhoneNumber: '',
    emailAddress: '',
    
    permanentCountry: '',
    permanentAddressLine1: '',
    permanentAddressLine2: '',
    permanentAddressLine3: '',
    permanentSuburb: '',
    permanentState: '',
    permanentPostcode: '',
    permanentHasAddressChanged: false,

    knowsAustralianAddress: 'N',
    contactAddressSameAsPermanentAddress: false,
    contactCountry: '',
    contactAddressLine1: '',
    contactAddressLine2: '',
    contactAddressLine3: '',
    contactSuburb: '',
    contactState: '',
    contactPostcode: '',
    contactHasAddressChanged: false,

    contactName: '',
    contactNumber: '',
    contactRelationship: '',
    citizenshipCode: '',
    offshoreFlag: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(()=>{
    setValues(props.formDetails)

    getTooltipsForPage('Settings').then(tooltips => {
      setTooltips(tooltips)
    })

    setPronounsList(
      populateCreateListOptions(
        props.pronounsOptions.sort((pronuon1, pronoun2) => {
          if (pronuon1.sequenceNumber > pronoun2.sequenceNumber) {
            return 1
          }
          if (pronuon1.sequenceNumber < pronoun2.sequenceNumber) {
            return -1
          }

          return 0
        })
      )
    )
      setAffirmedGenderList(
        populateCreateListOptions(
          props.affirmedGenderOptions.sort((gender1, gender2) => {
            if (gender1.sequenceNumber > gender2.sequenceNumber) {
              return 1
            }
            if (gender1.sequenceNumber < gender2.sequenceNumber) {
              return -1
            }
            return 0
          })
        )
      )
  }, [props]) 

  const populateCreateListOptions = (listOptions) => {
    let options = []
    listOptions.map(item => options.push({ label: item.description, value: item.code }))
    return options
  }

  const handleChange = async (e) => {

    const { name, value } = e.target;

    setValues({
      ...values,
      [name]: value
    });

    let schemaDescription = validationSchema.describe();

    let hasFields = name in schemaDescription.fields;

    if (hasFields) {
      try {
        await validationSchema.validateAt(name, { ...values, [name]: value }, { context: values });
        setErrors({
          ...errors,
          [name]: undefined
        });
      } catch (validationError) {
        setErrors({
          ...errors,
          [name]: validationError.message
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await validationSchema.validate(values, { context: values , abortEarly: false });
      setErrors({});
      props.onSubmit({form: values, valid: true, errors: errors})
    } catch (validationErrors) {
      const formattedErrors = validationErrors.inner.reduce((acc, error) => {
        return { ...acc, [error.path]: error.message };
      }, {});
      setErrors(formattedErrors);
      props.onSubmit({form: values, valid: false, errors: errors})
    }
  };

  const handleAustralianAddressKnowledgeChange = async (e) => {
    const { name, value } = e.target;

    setValues({
      ...values,
      knowsAustralianAddress: value,
      contactAddressSameAsPermanentAddress:
        value === 'Y' ? false : value.contactAddressSameAsPermanentAddress
    })
  }

  const handleCountryChange = (countryFieldName, countryCode) => {

    if (countryFieldName === 'permanentCountry' && countryCode) {
      setValues({
        ...values,
        permanentCountry: countryCode,
        permanentAddressLine1: '',
        permanentAddressLine2: '',
        permanentAddressLine3: '',
        permanentSuburb: '',
        permanentState: '',
        permanentPostcode: '',
        permanentLatitude: 0,
        permanentLongitude: 0,
        permanentVerificationDate: '',
        permanentValidationStatus: 'MANUAL'
      })
    }

    if (countryFieldName === 'contactCountry' && countryCode) {
      setValues({
        ...values,
        contactCountry: countryCode,
        contactAddressLine1: '',
        contactAddressLine2: '',
        contactAddressLine3: '',
        contactSuburb: '',
        contactState: '',
        contactPostcode: '',
        contactLatitude: 0,
        contactLongitude: 0,
        contactVerificationDate: '',
        contactValidationStatus: 'MANUAL'
      })
    }

  }

  const handleInsertAddressManually = event => {

    const { name, checked } = event.target;

    if (name === 'insertPermanentAddressManually') {
      setValues({
        ...values,
        permanentLatitude: values.insertPermanentAddressManually ? 0 : values.permanentLatitude,
        permanentLongitude: values.insertPermanentAddressManually ? 0 : values.contactLongitude,
        permanentVerificationDate: checked ? '' : values.permanentVerificationDate,
        permanentValidationStatus: checked ? 'MANUAL' : values.permanentValidationStatus,
        insertPermanentAddressManually: checked
      })
    } else {
      setValues({
        ...values,
        contactLatitude: values.insertContactAddressManually ? 0 : values.contactLatitude,
        contactLongitude: values.insertContactAddressManually ? 0 : values.contactLongitude,
        contactVerificationDate: checked ? '' : values.contactVerificationDate,
        contactValidationStatus: checked ? "MANUAL" : values.contactValidationStatus,
        insertContactAddressManually: checked
      })
    }
  }

  const handleAddressValidation = ({ addressLine1, addressLine2, addressLine3, city, state, postCode, country, longitude, latitude, verificationDate, validationStatus }, name) => {

    if(name === 'permanentAddressValidator') {
      setValues({
        ...values,
        permanentAddressLine1: addressLine1,
        permanentAddressLine2: addressLine2,
        permanentAddressLine3: addressLine3,
        permanentSuburb: city,
        permanentState: state,
        permanentPostcode: postCode,
        permanentLatitude: latitude || 0,
        permanentLongitude: longitude || 0,
        permanentVerificationDate: verificationDate || '',
        permanentValidationStatus: validationStatus,
        insertPermanentAddressManually: false
      })
    } else {
      setValues({
        ...values,
        contactAddressLine1: addressLine1,
        contactAddressLine2: addressLine2,
        contactAddressLine3: addressLine3,
        contactSuburb: city,
        contactState: state,
        contactPostcode: postCode,
        contactLatitude: latitude || 0,
        contactLongitude: longitude || 0,
        contactVerificationDate: verificationDate || '',
        contactValidationStatus: validationStatus,
        insertContactAddressManually: false
      })
    }
  }

  const handleSameAsPermanentChange = event => {

    const { name, checked } = event.target;

    if (!checked) {
      const contactAddress = getDefaultContactAddress(props.autofillAddresses)
      if (contactAddress) {
        setValues({
          ...values,
          [name]: checked,
          ...mergeContactDetails(contactAddress)
        })
      }
    } else {
      setValues({
        ...values,
        [name]: checked,
      })
    }
  }

  const showContactAddress = () => 
    (isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) && values.knowsAustralianAddress === 'Y') 
      || !(isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) 
      || values.contactAddressSameAsPermanentAddress)

  const toggleChosenDetails = () => setHideChosenDetaiils(!hideChosenDetaiils)

  const handleChosenFirstNameCloseModal = () => setShowChosenFirstNameModal(false)

  const handleChosenLastNameCloseModal = () => setShowChosenLastNameModal(false)

  const handleChosenPronounsCloseModal = () => setShowChosenPronounsModal(false)

  const handleChosenAffirmedGenderCloseModal = () => setShowChosenAffirmedGenderModal(false)


  return (
      <div>
        <Modal className={'modal modal-lg'} isOpen={showChosenFirstNameModal} handleCloseModal={() => handleChosenFirstNameCloseModal()} title={'Chosen First Name'}>
          <div>
            <div className="form-help-text">
              You can update your Chosen First Name or Preferred First name to be used at VU instead of your Legal Given Name. The name provided in this field will appear in your VU email
              address and on your VU accounts.
            </div>

            <div className="form-help-text">
              <a href="https://www.vu.edu.au/contact-us" target="_blank">
                <strong>VUHQ</strong>
              </a>{' '}
              or{' '}
              <a href="https://www.vu.edu.au/current-students/campus-life/advice-support/welfare-legal-services" target="_blank">
                <strong>Student Welfare team</strong>
              </a>{' '}
              can help you reach out to staff to make sure you are addressed by this name.
            </div>
          </div>
        </Modal>
        <Modal className={'modal modal-lg'} isOpen={showChosenLastNameModal} handleCloseModal={() => handleChosenLastNameCloseModal()} title={'Chosen Last Name'}>
          <div>
            <div className="form-help-text">
              You can update your Chosen Last Name or Preferred Last name to be used at VU instead of your Legal Surname. The name provided in this field will appear in your VU email address
              and on your VU accounts.
            </div>
            <div className="form-help-text">
              <a href="https://www.vu.edu.au/contact-us" target="_blank">
                <strong>VUHQ</strong>
              </a>{' '}
              or{' '}
              <a href="https://www.vu.edu.au/current-students/campus-life/advice-support/welfare-legal-services" target="_blank">
                <strong>Student Welfare team</strong>
              </a>{' '}
              can help you reach out to staff to make sure you are addressed by this name.
            </div>
          </div>
        </Modal>
        <Modal className={'modal modal-lg'} isOpen={showChosenPronounsModal} handleCloseModal={() => handleChosenPronounsCloseModal()} title={'Pronouns'}>
          <div>
            <div className="form-help-text">Your Pronouns are what other people refer to you as when they're not using your name. </div>
            <div className="form-help-text">Select 'Prefer not to say' if you do not want to display your pronouns in IT systems such as Outlook, E learning, MyVU etc.</div>
          </div>
        </Modal>
        <Modal className={'modal modal-lg'} isOpen={showChosenAffirmedGenderModal} handleCloseModal={() => handleChosenAffirmedGenderCloseModal()} title={'Affirmed Gender'}>
          <div>
            <div className="form-help-text">
              Gender is part of a person’s personal and social identity. It refers to the way a person feels, presents and is recognised within a community. Affirmed Gender will be displayed
              only on MyVU Personal Details page for self-visibility purpose.
            </div>
          </div>
        </Modal>
        <Modal 
          isOpen={props.showInvalidAddressModal} 
          handleCloseModal={() => props.handleCloseInvalidAddressModal()} 
          title={'You have an unverified address!'} 
          showConfirmationButton={true}
          buttonText="I understand"
        >
          <div className="my-20">
            <p>
              Please note that your
              {values.permanentValidationStatus !== AddressValidationStatus.VALIDATED && values.contactValidationStatus !== AddressValidationStatus.VALIDATED
                ? ' permanent and contact addresses '
                : values.permanentValidationStatus !== AddressValidationStatus.VALIDATED
                ? ' permanent address '
                : values.contactValidationStatus !== AddressValidationStatus.VALIDATED
                ? ' contact address '
                : null}
              could not be automatically verified. To avoid incorrect information in your student record, please try again using the 'Find Address' field to ensure we can verify your address
              correctly. If you're unable to find your address, you may still proceed manually with your changes by clicking 'Save' again.
            </p>
          </div>
        </Modal>
        <form onSubmit={handleSubmit}>
          <div className="formGroup question-group">
            <div className="header-with-model">
              <h2 onClick={() => toggleChosenDetails()}>
                Chosen details
                {!hideChosenDetaiils ? <ExpandLess /> : <ExpandMore />}
              </h2>
              <HelpText
                tag={'h3'}
                text={
                  <div>
                    <p>*These fields are NOT mandatory*</p>
                    <p>
                      At VU, we recognise that some students may use a name that is different to their legal name. By providing your Chosen Details below we can support you in ensuring how
                      you are addressed reflects who you truly are.
                    </p>
                    <p>Our systems will use your legal names where no Chosen Name is provided or where it is necessary for legal verification purposes.</p>
                  </div>
                }
                toolTip={null}
              />
            </div>
            <div className={hideChosenDetaiils ? 'hide' : ''}>
              {/* <div className="form-top-help-text">Provide your chosen details in this section.</div> */}
              <div className="row">
                <div>
                  <label className={errors.chosenFirstName ? 'form-error' : 'input-custom-label'}>
                    Chosen first name
                    <IconButton className="action-info-model" onClick={() => setShowChosenFirstNameModal(true)}>
                      <Info className="material-icons" />
                    </IconButton>
                  </label>
                  <TextField
                    error={errors.chosenFirstName}
                    helperText={errors.chosenFirstName}
                    maxLength={30}
                    fullWidth
                    name="chosenFirstName"
                    onChange={handleChange}
                    type="text"
                    value={values.chosenFirstName}
                    autoComplete="chosen-first-name"
                    variant="standard"
                  />
                </div>
                <div>
                  <label className={errors.chosenLastName ? 'form-error' : 'input-custom-label'} htmlFor={'chosenLastNameId'}>
                    Chosen last name
                    <IconButton className="action-info-model" onClick={() => setShowChosenLastNameModal(true)}>
                      <Info className="material-icons" />
                    </IconButton>
                  </label>
                  <TextField
                    id={'chosenLastNameId'}
                    error={errors.chosenLastName}
                    helperText={errors.chosenLastName}
                    maxLength={30}
                    fullWidth
                    name="chosenLastName"
                    onChange={handleChange}
                    type="text"
                    value={values.chosenLastName}
                    autoComplete="chosen-last-name"
                    variant="standard"
                  />
                </div>
              </div>
              <div className="row">

                <FormControl fullWidth>
                  <label className={errors.chosenPronouns ? 'form-error' : 'dropdown-label'} htmlFor='chosenPronounsID'>
                    Pronouns
                    <IconButton className="action-info-model" onClick={() => setShowChosenPronounsModal(true)}>
                      <Info className="material-icons" />
                    </IconButton>
                  </label>
                  <Select
                    id="chosenPronounsID"
                    className={errors.chosenPronouns ? 'form-error' : ''}
                    name="chosenPronouns"
                    value={values.chosenPronouns}
                    options={pronounsList}
                    onChange={handleChange}
                    variant="standard"
                    sx={{marginTop: '0 !important'}}
                  >
                    { !pronounsList.length ? null : pronounsList.map(pronoun => {
                      return (
                        <MenuItem key={pronoun.value} value={pronoun.value}>{pronoun.label}</MenuItem>
                      )
                    })}
                  </Select>
                  {/* {errors.chosenPronouns ? <FormHelperText className="form-error">{errors.chosenPronouns}</FormHelperText> : false} */}
                </FormControl>

                <FormControl fullWidth>
                  <label className={errors.chosenAffirmedGender ? 'form-error' : 'dropdown-label'} htmlFor='chosenAffirmedGenderID'>
                    Affirmed Gender
                    <IconButton className="action-info-model" onClick={() => setShowChosenAffirmedGenderModal(true)}>
                      <Info className="material-icons" />
                    </IconButton>
                  </label>
                  <Select
                    id='chosenAffirmedGenderID'
                    className={errors.chosenAffirmedGender ? 'form-error' : ''}
                    name="chosenAffirmedGender"
                    value={values.chosenAffirmedGender}
                    options={affirmedGenderList}
                    onChange={handleChange}
                    variant="standard"
                    sx={{marginTop: '0 !important'}}
                  >
                    { !affirmedGenderList.length ? null : affirmedGenderList.map(gender => {
                      return (
                        <MenuItem key={gender.value} value={gender.value}>{gender.label}</MenuItem>
                      )
                    })}
                  </Select>
                  {/* {errors.chosenAffirmedGender ? <FormHelperText className="form-error">{errors.chosenAffirmedGender}</FormHelperText> : false} */}
                </FormControl>

              </div>
            </div>
          </div>
          <div className="formGroup question-group">
            <HelpText tag={'h3'} text={'Please confirm your chosen/preferred name.'} toolTip={getTooltipBySlug('preferred-name', tooltips)} />
            <div className="row">
              <div>
                <TextField
                  error={!!errors.preferredName}
                  helperText={errors.preferredName}
                  label="Preferred name"
                  fullWidth
                  maxLength={30}
                  name="preferredName"
                  onChange={handleChange}
                  type="text"
                  value={values.preferredName}
                  autoComplete="given-name"
                  variant='standard'
                />
              </div>
            </div>
          </div>
          <div className="formGroup question-group">
            <h2>Contact details</h2>
            <div className="row">
              <div>
                <TextField
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  label="Mobile number"
                  fullWidth
                  name="mobileNumber"
                  onChange={handleChange}
                  type="tel"
                  value={values.mobileNumber}
                  autoComplete="tel"
                  variant="standard"
                />
              </div>
              <div>
                <TextField
                  error={!!errors.homePhoneNumber}
                  helperText={errors.homePhoneNumber}
                  label="Home phone number (optional)"
                  fullWidth
                  name="homePhoneNumber"
                  onChange={handleChange}
                  type="tel"
                  value={values.homePhoneNumber}
                  autoComplete="tel"
                  variant="standard"
                />
              </div>
            </div>
            <div className="row">
              <div>
                <TextField
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress}
                  label="Personal email"
                  fullWidth
                  name="emailAddress"
                  onChange={handleChange}
                  type="email"
                  value={values.emailAddress}
                  autoComplete="email"
                  variant="standard"
                />
              </div>
            </div>
          </div>
          <h2>Address details</h2>
          <div className="formGroup question-group">
            <HelpText tag={'h3'} text={'Permanent home address'} toolTip={getTooltipBySlug('permanent-home-address', tooltips)} />

            <Address
              allowedCountries={isDomesticStudent(values.stuCitizenCd) ? 'AUS' : isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) ? 'OVERSEAS' : 'ALL'}
              country={values.permanentCountry}
              countryName="permanentCountry"
              countryOnChange={handleCountryChange}
              countryErrors={errors.permanentCountry}
              insertAddressManually={values.insertPermanentAddressManually}
              insertAddressManuallyName="insertPermanentAddressManually"
              handleInsertAddressManuallyChange={handleInsertAddressManually}
              addressValidatorName="permanentAddressValidator"
              handleAddressValidation={handleAddressValidation}
              currentValidationStatus={values.permanentValidationStatus}
              showInvalidAddressAlert={props.hasAttemptedToSave}
              addressLine1={values.permanentAddressLine1}
              addressLine1Name="permanentAddressLine1"
              addressLine1Errors={errors.permanentAddressLine1}
              addressLine1OnChange={handleChange}
              addressLine2={values.permanentAddressLine2}
              addressLine2Name="permanentAddressLine2"
              addressLine2Errors={errors.permanentAddressLine2}
              addressLine2OnChange={handleChange}
              addressLine3={values.permanentAddressLine3}
              addressLine3Name="permanentAddressLine3"
              addressLine3Errors={errors.permanentAddressLine3}
              addressLine3OnChange={handleChange}
              city={values.permanentSuburb}
              cityName="permanentSuburb"
              cityErrors={errors.permanentSuburb}
              cityOnChange={handleChange}
              state={values.permanentState}
              stateName="permanentState"
              stateErrors={errors.permanentState}
              stateOnChange={handleChange}
              postcode={values.permanentPostcode}
              postcodeName="permanentPostcode"
              postcodeErrors={errors.permanentPostcode}
              postcodeOnChange={handleChange}
              excludedCountries={props.excludedCountries}
            />
          </div>

          {isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) && (
            <div className="question-group">
              <HelpText tag="h3" text="Do you know where you will be living in Australia?" />
              <div className="form-items-group">
                <div className="formGroup formGroup--narrow">
                  <RadioGroup
                    name="knowsAustralianAddress"
                    onChange={handleAustralianAddressKnowledgeChange}
                    value={values.knowsAustralianAddress}
                  >
                    <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                    <FormControlLabel value="N" control={<Radio />} label="No" />
                  </RadioGroup>
                </div>
              </div>

              {values.knowsAustralianAddress === 'N' ? (
                <div className="sidenote sidenote--warning">
                  <div className="sidenote__content">
                    <p>
                      <strong>Important:</strong> You need to provide your Australian address to comply with your visa conditions. Please ensure you provide your address details within seven
                      days of arriving in Australia. If you change your address while in Australia, you are required to provide details of your new address within seven days of moving.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <div className="formGroup question-group">
            {(!isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) || (isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) && values.knowsAustralianAddress === 'Y')) && (
              <div>
                <HelpText tag={'h3'} text={'Contact address'} toolTip={getTooltipBySlug('contact-address', tooltips)} />
              </div>
            )}
            {!isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) ? (
              <div className="form-items-group">
                <FormGroup>
                  <FormControlLabel 
                    control={
                      <Checkbox
                        name="contactAddressSameAsPermanentAddress"
                        className="checkbox postalSameAsPermanent"
                        checked={values.contactAddressSameAsPermanentAddress}
                        onChange={handleSameAsPermanentChange}
                      />
                    }
                    label="Same as permanent address"
                  />
                </FormGroup>
              </div>
            ) : null}

            <Address
              allowedCountries={isInternationalOnshoreStudent(values.stuCitizenCd, values.offshoreFlag) || isDomesticStudent(values.stuCitizenCd) ? 'AUS' : 'ALL'}
              enabled={showContactAddress()}
              country={values.contactCountry}
              countryName="contactCountry"
              countryOnChange={handleCountryChange}
              countryErrors={errors.contactCountry}
              insertAddressManually={values.insertContactAddressManually}
              insertAddressManuallyName="insertContactAddressManually"
              handleInsertAddressManuallyChange={handleInsertAddressManually}
              addressValidatorName="contactAddressValidator"
              handleAddressValidation={handleAddressValidation}
              currentValidationStatus={values.contactValidationStatus}
              showInvalidAddressAlert={props.hasAttemptedToSave}
              addressLine1={values.contactAddressLine1}
              addressLine1Name="contactAddressLine1"
              addressLine1Errors={errors.contactAddressLine1}
              addressLine1OnChange={handleChange}
              addressLine2={values.contactAddressLine2}
              addressLine2Name="contactAddressLine2"
              addressLine2Errors={errors.contactAddressLine2}
              addressLine2OnChange={handleChange}
              addressLine3={values.contactAddressLine3}
              addressLine3Name="contactAddressLine3"
              addressLine3Errors={errors.contactAddressLine3}
              addressLine3OnChange={handleChange}
              city={values.contactSuburb}
              cityName="contactSuburb"
              cityErrors={errors.contactSuburb}
              cityOnChange={handleChange}
              state={values.contactState}
              stateName="contactState"
              stateErrors={errors.contactState}
              stateOnChange={handleChange}
              postcode={values.contactPostcode}
              postcodeName="contactPostcode"
              postcodeErrors={errors.contactPostcode}
              postcodeOnChange={handleChange}
              excludedCountries={props.excludedCountries}
            />
          </div>
          <h2>Emergency contact</h2>
          <div className="formGroup question-group">
            <div className="row">
              <div>
                <TextField
                  error={!!errors.contactName}
                  helperText={errors.contactName}
                  label="Contact name"
                  fullWidth
                  maxLength={40}
                  name="contactName"
                  onChange={handleChange}
                  type="text"
                  value={values.contactName}
                  variant="standard"
                />
              </div>
            </div>
            <div className="row">
              <div>
                <TextField
                  error={!!errors.contactNumber}
                  helperText={errors.contactNumber}
                  label="Contact number"
                  fullWidth
                  name="contactNumber"
                  onChange={handleChange}
                  type="tel"
                  value={values.contactNumber}
                  variant="standard"
                />
              </div>
              <div>
                <FormControl variant='standard' fullWidth>
                  <InputLabel 
                    id="contact-relationship-select" 
                    error={!!errors.contactRelationship}
                  >
                    Relationship
                  </InputLabel>
                  <Select
                    labelId='contact-relationship-select'
                    //required={true}
                    error={!!errors.contactRelationship}
                    //className={errors.contactRelationship ? 'form-error' : ''}
                    label="Relationship"
                    name="contactRelationship"
                    value={values.contactRelationship}
                    options={props.emergencyContactOptions}
                    onChange={handleChange}
                  >
                    { !props.emergencyContactOptions.length ? null : props.emergencyContactOptions.map(relationship => {
                      return (
                        <MenuItem key={relationship.code} value={relationship.code}>{relationship.description}</MenuItem>
                      )
                    })}
                  </Select>
                  {!!errors.contactRelationship ? <FormHelperText htmlFor='contact-relationship-select' error>{errors.contactRelationship}</FormHelperText> : false}
                </FormControl>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-md-6">
              {props.saving ? (
                <button className="btn btn-lg btn-primary" disabled>
                  Saving...
                </button>
              ) : (
                <button className="btn btn-lg btn-primary">Save</button>
              )}
            </div>

            <div className="col-xs-12 col-md-6">
              <div className={props.formSubmitted ? 'show loader' : 'hide loader'}>
                <svg className="circular">
                  <circle className="path" cx="50" cy="50" r="20" fill="none" strokeWidth="2" stroke-color="#f00" strokeMiterlimit="10" />
                </svg>
                <svg className="suc">
                  <path className="checkmark__check" fill="none" d="M10.7,20.4l5.3,5.3l12.4-12.5" />
                </svg>
              </div>
            </div>
          </div>
        </form>
      </div>
    )
}
export default SettingsForm;